import { motion } from 'framer-motion';
import { Flame, Video, TrendingUp } from 'lucide-react';

export default function DareExamples() {
    const dares = [
        {
            title: 'Voice Impressions',
            description: 'Do impressions of 3 famous people or characters',
            difficulty: 'MEDIUM',
            category: 'Other',
            videos: 2,
            trending: true,
        },
        {
            title: 'No Blinking Challenge',
            description: 'Try to go as long as you can without blinking',
            difficulty: 'MEDIUM',
            category: 'Challenge',
            videos: 1,
            trending: true,
        },
        {
            title: 'Balance on One Foot',
            description: 'Balance on one foot for as long as possible',
            difficulty: 'MEDIUM',
            category: 'Fitness',
            videos: 1,
            trending: false,
        },
        {
            title: 'Silent Film Acting',
            description: 'Act out a scene using only expressions and gestures',
            difficulty: 'MEDIUM',
            category: 'Comedy',
            videos: 0,
            trending: false,
        },
        {
            title: 'Show Your Talent',
            description: 'Show off any hidden talent you have',
            difficulty: 'EASY',
            category: 'Talent',
            videos: 0,
            trending: false,
        },
        {
            title: 'Water Cup Balance',
            description: 'Balance a cup of water on your head while walking',
            difficulty: 'MEDIUM',
            category: 'Challenge',
            videos: 0,
            trending: false,
        },
    ];

    const getDifficultyStyles = (difficulty: string) => {
        switch (difficulty) {
            case 'EASY':
                return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 'MEDIUM':
                return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'HARD':
                return 'bg-red-500/20 text-red-400 border-red-500/30';
            default:
                return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    return (
        <section className="py-24 gradient-bg">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-primary-500/10 text-primary-400 rounded-full text-sm font-semibold border border-primary-500/20">
                        <Flame className="w-4 h-4" />
                        Trending Dares
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
                        Get Inspired: Real Dares from DareUp
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        From silly to impressive, there's a dare for everyone
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {dares.map((dare, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            className="group bg-dark-800 rounded-2xl p-6 shadow-lg hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-dark-700"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                                        <Flame className="w-5 h-5 text-primary-500" />
                                    </div>
                                    {dare.trending && (
                                        <div className="flex items-center gap-1 px-2 py-1 bg-primary-500/10 rounded-lg">
                                            <TrendingUp className="w-3 h-3 text-primary-400" />
                                            <span className="text-xs font-medium text-primary-400">Hot</span>
                                        </div>
                                    )}
                                </div>
                                <span
                                    className={`px-3 py-1 ${getDifficultyStyles(dare.difficulty)} border rounded-full text-xs font-bold`}
                                >
                                    {dare.difficulty}
                                </span>
                            </div>

                            {/* Content */}
                            <h3 className="font-display text-lg font-bold text-white mb-2 group-hover:text-primary-400 transition-colors">
                                {dare.title}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">{dare.description}</p>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-4 border-t border-dark-700">
                                <span className="text-xs text-gray-500">{dare.category}</span>
                                <div className="flex items-center gap-1 text-gray-500">
                                    <Video className="w-3.5 h-3.5" />
                                    <span className="text-xs">{dare.videos} videos</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-center mt-12"
                >
                    <p className="text-gray-400">
                        <strong className="text-white">50+ dares</strong> ready at launch, with new ones added weekly
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
